import React from 'react';
import cx from 'classnames';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { ArticleBlock, ArticleFeed, } from '@/components/Articles';
import Button from '@/components/Button';
import ContentstackImage from '@/components/ContentstackImage';
import SocialShare from '@/components/SocialShare';
import { JOBS_ARTICLES_TAGS } from '@/constants/jobs';
// TODO: This is here to guard rendering of StaticShare.  It only succeeds on the client (and may be
// brittle there anyway because of use of `location`, which may or may not be defined outside of hooks).
// We should fix that component not to be dependent on `location`, or make it static-render-safe.
import { client } from "@/env";
import { useTranslation } from '@/hooks/translation';
import { useCurrentUser } from '@/hooks/user';
import { ChevronLeft } from '@/icons';
import { articleType } from '@/types';
import { prioritizeArticles } from '@/utilities/articles';
import { formatLocalDateSimple } from '@/utilities/datetime';
import { presence } from '@/utilities/string';
import Actions from './Actions';
import styles from './fullPageSingleArticle.module.scss';

const customSeoMeta = (content) => {
  const meta = content?.seo?.meta || [];

  return meta.map(item => (
    <meta
      content={item.content}
      key={item.name}
      property={item.name}
    />
  ));
};

export default function FullPageSingleArticle({ article, relatedArticles }) {
  const { back, push } = useRouter();
  const { t } = useTranslation();
  const { user, isLoggedIn } = useCurrentUser();

  const seoTitle = presence(article?.seo?.article_title_tag);
  const seoDescription = presence(article?.seo?.article_description_tag) || article?.article_description;
  const title = article && (seoTitle || article.title);
  const image = article && (article.hero_image.image || article.thumbnail);
  const imageType = image?.content_type;

  function isLocked() {
    return !user?.is_premium_member && article?.is_premium;
  }

  const isJobsArticle = JOBS_ARTICLES_TAGS.some(tag => article.tags.includes(tag));

  const handleClick = () => {
    return isJobsArticle ? back() : push('/articles');
  };

  return (
    <div className={styles.articleContainer}>
      {article && image && (
        <>
          <Head>
            <title>{title}</title>
            <meta content={article?.title} name="title" key="og:title" property="og:title" />
            <meta content={seoDescription} name="description" key="og:description" property="og:description" />
            <meta content={seoDescription} key="description" property="description" />
            <meta content={image?.url} key="og:image" property="og:image" />
            <meta content={imageType} property="og:image:type" />
            <meta content="550" property="og:image:height" />
            <meta content="338" property="og:image:width" />
            <meta content="article" key="og:type" property="og:type" />
            <meta content="summary_large_image" key="twitter:card" property="twitter:card" />
            <meta content="SABEResPODER" key="twitter:title" property="twitter:title" />
            <meta content={seoDescription} key="twitter:description" property="twitter:description" />
            <meta content={image?.url} key="twitter:image" property="twitter:image" />
            <meta content="@SABEResPODER" key="twitter:site" property="twitter:site" />
            {customSeoMeta(article)}
          </Head>
          <div className={cx(styles.backButton, "mb-3 mx-auto")}>
            <Button onClick={handleClick} className={styles.backButton}>
              <ChevronLeft />
              <span className="ms-2">{t('articles.template.return_to_guides')}</span>
            </Button>
          </div>
          <div className={cx(styles.headerWrapper, "mb-4 mx-auto")}>
            <ContentstackImage
              className={`${styles.headerBackground}`}
              alt={image.title}
              height={300}
              width={518}
              src={image.url}
            />
          </div>
          <h1 className={cx(styles.articleTitle, "mx-auto")}>{article.title}</h1>
          <p className={cx(styles.articleCategory, "text-capitalize mx-auto")}>
            {t(`articles.categories.${article.article_category}`)}
          </p>
          <div className={cx(styles.articleAuthor, "mx-auto")}>
            <div className="d-flex align-items-center">
              <ContentstackImage
                className={styles.authorImage}
                alt={article.article_author.author_name}
                height={35}
                width={35}
                src={article?.article_author?.author_image?.url}
              />
              <p className="mb-0 ms-2"> {t("articles.by")} {article.article_author.author_name} | <span> {t("articles.published")} {formatLocalDateSimple(article.publish_details.time)} </span> </p>
            </div>

            <hr className={cx(styles.divider)} />
          </div>
          {client &&
            <SocialShare title={title} className={cx(styles.socialShare, "mx-auto")} />
          }
        </>
      )}
      <article>
        <div className={styles.articleContent}>
          {isLocked() &&
            <Actions isLoggedIn={isLoggedIn} />
          }
          {article && article.article_content && article.article_content.map((content, idx) =>
            <ArticleBlock key={idx} content={content} />
          )}
        </div>
        {article && (
          <div className={cx(styles.articleFeedWrapper, "my-2 mx-auto")}>
            <h2 className={styles.header}> {t(`articles.guides.${article.article_category}`)} </h2>
            <ArticleFeed articles={prioritizeArticles(relatedArticles)} />
          </div>
        )}
      </article>
    </div>
  );
}

FullPageSingleArticle.propTypes = {
  article: articleType,
  relatedArticles: PropTypes.array,
};
